/**
 * DocExtractedFieldsPanel — the document's side of the extraction story.
 *
 * The review surfaces built so far are REQUIREMENT-scoped (Evidence ledger
 * cards, Coverage Matrix detail). This is the inverse the doc drawer was
 * missing: "what did THIS document put into the evidence, where is it used,
 * and let me rule on it right here." Same rows, same store action, same
 * review-as-metadata contract — a decision never promotes the evidence
 * tier, and reject removes the value (D11 keeps re-scans from resurrecting
 * it).
 *
 * Data source: the canonical SED's per-section `provenanceDetail` where
 * `docId` matches this document — i.e. the sections whose last stamp came
 * from it. Sections merely SUPPORTED by the doc (stamped by another doc)
 * are listed as linkage so "where it is used" stays complete.
 */
import React from 'react';
import { toast } from 'sonner';
import type { SiteDocument } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import type { StructuredEvidenceValue } from '../../types/siteEvidence';
import { SED_SECTION_IDS } from '../../types/siteEvidence';
import { SED_SECTION_LABELS } from '../../lib/sedStaleness';
import { documentMappingSummary } from '../../lib/documentMappingOutcome';
import { documentFieldEntries, currentDocumentFieldSource, unitForDocumentField } from '../../lib/sed/documentFieldView';
import type { DocFieldSource } from '../../types/siteEvidence';
import { useStore } from '../../store';
import {
  ExtractedFieldReviewPanel,
} from './coverage/ExtractedFieldReviewPanel';
import type {
  ExtractedFieldRow,
  FieldReview,
  FieldReviewDecision,
} from '../../lib/coverage/extractedFieldReview';

interface SectionRows {
  sectionId: string;
  label: string;
  rows: ExtractedFieldRow[];
  adopted: Array<{ field: string; source: DocFieldSource }>;
}

interface LoadedState {
  sections: SectionRows[];
  /** Sections the doc supports whose stamps belong to another doc. */
  linkedOnly: string[];
}

function rowsForDoc(
  sed: Record<string, unknown>,
  docId: string,
): LoadedState {
  const sections: SectionRows[] = [];
  const linkedOnly: string[] = [];
  for (const sectionId of SED_SECTION_IDS) {
    const slot = sed[sectionId] as {
      data?: Record<string, unknown>;
      docFieldSources?: Record<string, DocFieldSource>;
      supportingDocIds?: string[];
      linkedDocumentIds?: string[];
      provenanceDetail?: {
        docId?: string;
        autoFillFields?: Record<string, StructuredEvidenceValue>;
        autoFillFieldSources?: Record<string, { docId: string }>;
        fieldReviews?: Record<string, FieldReview>;
      };
    } | undefined;
    if (!slot) continue;
    const detail = slot.provenanceDetail;
    const adopted = documentFieldEntries(sectionId, slot.data ?? {}).flatMap(([field]) => {
      const source = currentDocumentFieldSource(slot, field);
      return source?.docId === docId ? [{ field, source }] : [];
    });
    const rows: ExtractedFieldRow[] = [];
    if (detail?.autoFillFields || detail?.fieldReviews) {

      const keys = new Set([
        ...Object.keys(detail.autoFillFields ?? {}),
        ...Object.keys(detail.fieldReviews ?? {}),
      ]);
      for (const fieldKey of keys) {
        // The last section stamp is not the source of every field in a shared
        // section. A present per-field binding always wins over the legacy id.
        const sourceId = detail?.autoFillFieldSources?.[fieldKey]?.docId ?? detail?.docId;
        if (sourceId !== docId) continue;
        rows.push({
          fieldPath: `${sectionId}.${fieldKey}`,
          sectionId,
          fieldKey,
          value: detail?.autoFillFields?.[fieldKey],
          review: detail?.fieldReviews?.[fieldKey],
        });
      }
    }
    if (rows.length > 0 || adopted.length > 0) {
      sections.push({ sectionId, label: SED_SECTION_LABELS[sectionId as keyof typeof SED_SECTION_LABELS] ?? sectionId,
        rows: rows.sort((a, b) => a.fieldKey.localeCompare(b.fieldKey)), adopted });
      continue;
    }
    const linked = (slot.supportingDocIds ?? []).includes(docId)
      || (slot.linkedDocumentIds ?? []).includes(docId);
    if (linked) {
      linkedOnly.push(SED_SECTION_LABELS[sectionId as keyof typeof SED_SECTION_LABELS] ?? sectionId);
    }
  }
  return { sections, linkedOnly };
}

export const DocExtractedFieldsPanel: React.FC<{
  doc: SiteDocument;
  site: SiteEntity;
}> = ({ doc, site }) => {
  const [state, setState] = React.useState<LoadedState | 'loading' | 'unavailable'>('loading');
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!site.sedId) { setState('unavailable'); return; }
      try {
        const { loadSedById } = await import('../../services/sedLoader');
        const sed = await loadSedById(site.sedId, site.sedStoragePath, site.sedVersion);
        if (cancelled) return;
        if (!sed) { setState('unavailable'); return; }
        setState(rowsForDoc(sed as unknown as Record<string, unknown>, doc.id));
      } catch (e) {
        console.warn('[DocExtractedFieldsPanel] SED load failed:', (e as Error)?.message);
        if (!cancelled) setState('unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id, site.sedId, site.sedStoragePath, site.sedVersion, reloadNonce]);

  const decide = React.useCallback(async (
    sectionId: string,
    fieldKey: string,
    decision: FieldReviewDecision | null,
    correctedValue?: StructuredEvidenceValue,
  ) => {
    if (busyKey) return;
    setBusyKey(fieldKey);
    try {
      const res = await useStore.getState().reviewExtractedField(
        site.id, sectionId, fieldKey, decision, correctedValue,
      );
      if (!res.ok) {
        toast.error(res.reason ?? 'The review could not be saved.');
        return;
      }
      toast.success(
        decision === null ? 'Review undone.'
          : decision === 'rejected' ? 'Marked wrong — the value was removed.'
            : decision === 'corrected' ? 'Value corrected.'
              : 'Confirmed against the document.',
      );
      setReloadNonce(n => n + 1);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'The review could not be saved.');
    } finally {
      setBusyKey(null);
    }
  }, [site.id, busyKey]);

  if (state === 'loading') {
    return (
      <p className="type-body text-text-muted" data-testid="doc-extracted-fields-loading">
        Checking extracted fields…
      </p>
    );
  }
  if (state === 'unavailable') {
    return (
      <p className="type-body text-text-muted" data-testid="doc-extracted-fields-unavailable">
        Extracted fields could not be checked — the evidence document is not
        available.
      </p>
    );
  }
  if (state.sections.length === 0 && state.linkedOnly.length === 0) {
    const outcomes = doc.extractionOutcome?.version === 1 && Array.isArray(doc.extractionOutcome.targets)
      ? documentMappingSummary(doc.extractionOutcome) : undefined;
    const explanation = !outcomes
      ? 'No evidence fields have been extracted from this document yet — use Scan now on its card to extract.'
      : outcomes.failed > 0
        ? 'No retained fields are available here. Some mapping targets failed; retry those targets using the document card.'
        : outcomes.deferred > 0
          ? 'No retained fields are available here yet. Mapping targets remain queued; continue mapping using the document card.'
          : outcomes.rejected > 0
            ? 'No fields were adopted from the mapped source. Source scope, field policy or prior review checks prevented adoption; review those checks before retrying.'
            : outcomes.empty > 0
              ? 'Mapping completed without eligible field values from this source. Missing source data stays unknown.'
              : outcomes.unsupported > 0
                ? 'The matched targets have no supported field schema. The document remains available as evidence; retrying cannot create missing source data.'
                : doc.extractionOutcome?.targets.length
                  ? 'Mapping was assessed, but no source-bound fields remain in the current evidence.'
                  : 'Field mapping has not been assessed for this document.';
    return (
      <p className="type-body text-text-muted" data-testid="doc-extracted-fields-empty">
        {explanation}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="doc-extracted-fields">
      {state.sections.map(section => (
        <div key={section.sectionId} data-testid={`doc-extracted-section-${section.sectionId}`}>
          <p className="type-meta text-text-muted mb-1">
            Used in evidence · <span className="text-text-secondary">{section.label}</span>
          </p>
          {section.adopted.length > 0 && (
            <dl className="space-y-2 type-body" data-testid={`doc-adopted-fields-${section.sectionId}`}>
              {section.adopted.map(({ field, source }) => (
                <div key={field} data-field-path={`${section.sectionId}.data.${field}`}>
                  <dt className="font-medium">{field.split('.').map(part => part.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())).join(' · ')}</dt>
                  <dd>{String(source.value)}{unitForDocumentField(field) ? ` ${unitForDocumentField(field)}` : ''}{source.pageNumber != null ? ` · page ${source.pageNumber}` : ''}</dd>
                  {source.excerptPreview && <dd className="text-text-secondary">“{source.excerptPreview}”</dd>}
                </div>
              ))}
            </dl>
          )}
          {section.rows.length > 0 && <ExtractedFieldReviewPanel
            rows={section.rows}
            documentNames={[doc.fileName]}
            busyFieldKey={busyKey}
            onDecide={(row, decision, corrected) => { void decide(row.sectionId, row.fieldKey, decision, corrected); }}
            onUndo={(row) => { void decide(row.sectionId, row.fieldKey, null); }}
          />}
        </div>
      ))}
      {state.linkedOnly.length > 0 && (
        <p className="type-body text-text-muted" data-testid="doc-extracted-linked-only">
          Also supports {state.linkedOnly.join(', ')} (values there were
          stamped by another document).
        </p>
      )}
    </div>
  );
};
